/**
 * @file categoryApi.js
 * @description Handles API operations related to categories, including:
 *              - Creating a new category with an image.
 *              - Updating an existing category's name and image.
 *              - Deleting a category by ID.
 *              Utilizes Axios for HTTP requests and FormData for file uploads.
 * @created 01-02-2025
 * @author Govind Dindewad
 */

import axios from "axios";
import apiUrl from "./api";

/**
 * Adds a new category with a category name and an image file.
 * 
 * @param {string} categoryName - The name of the category to be created.
 * @param {File} file - The image file to associate with the category.
 * @returns {Promise<Object|null>} - Axios response if successful, or null if an error occurred.
 */
async function postCategory(categoryName, file) {
    const token = window.localStorage.getItem('token');  // Get auth token from local storage

    // Create a fallback name for the image file if not provided
    const fileName = file.name || `category-image-${Date.now()}.png`;

    // Construct FormData to handle file and text data
    const formData = new FormData();
    formData.append("categoryName", categoryName);
    formData.append("categoryImageFile", file, fileName);

    try {
        // Make POST request to add a new category
        const response = await axios.post(`${apiUrl}secure/category/add-category`, formData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });

        return response;  // Return successful response

    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;  // Return null on error
    }
}

/**
 * Updates an existing category with a new name and optional image.
 * 
 * @param {string} categoryId - The ID of the category to update.
 * @param {string} categoryName - The new name for the category.
 * @param {string|null} categoryImage - Existing image URL (not used in request, optional).
 * @param {File|null} categoryImageFile - Optional new image file to replace the existing one.
 * @returns {Promise<number|null>} - HTTP status code if successful, or null on failure.
 */
async function updateCategory(categoryId, categoryName, categoryImage = null, categoryImageFile = null) {
    const token = window.localStorage.getItem('token');

    const formData = new FormData();
    formData.append("categoryName", categoryName);
    formData.append("categoryId", categoryId);

    // Only append a new image if it's a valid file object
    if (categoryImageFile instanceof Blob) {
        const fileName = categoryImageFile.name || `category-image-${Date.now()}.png`;
        formData.append("categoryImageFile", categoryImageFile, fileName);
    }

    try {
        // Make PUT request to update the category
        const response = await axios.put(`${apiUrl}secure/category/update-category`, formData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });

        return response.status;

    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return null;
    }
}

/**
 * Deletes a category using its unique ID.
 * 
 * @param {string} categoryId - The ID of the category to delete.
 * @returns {Promise<number|undefined>} - HTTP status code of the response, or undefined if an error occurred.
 */
async function deleteCategory(categoryId) {
    const token = window.localStorage.getItem('token');

    try {
        // Make DELETE request to remove the category
        const response = await axios.delete(`${apiUrl}secure/category/${categoryId}/delete-category`, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });

        return response.status;

    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error?.response?.status;
    }
}

// Export the API functions for external use
export { postCategory, updateCategory, deleteCategory };
